import { useTranslations } from '@vocab/react';
import {
  Autosuggest,
  filterSuggestions,
  IconSearch,
} from 'braid-design-system';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { MINIMUM_CHARACTERS_SEARCH } from '@seek/libs-shared';
import type { RoleTitle } from '../../../../shared/services/roleService/roleService';
import type { QuestionProps } from '../../../type';
import { JOB_TITLE_MAX_LENGTH } from '../../../validation/validationRules';
import translations from './.vocab';
export interface JobTitleValue {
  id?: string;
  text?: string;
}

type JobTitleQuestionProps = QuestionProps<JobTitleValue> & {
  getRoleTitles: (query: string) => Promise<RoleTitle[]>;
  onValueSelected: () => void;
  description?: string;
  setIsJobTitleUpdating?: (isUpdating: boolean) => void;
};

interface AutosuggestValue {
  text: string;
  description?: string;
  value?: string;
}

export const JobTitleQuestion = ({
  getRoleTitles,
  onChange,
  message,
  tone,
  value,
  description,
  onValueSelected,
  setIsJobTitleUpdating,
}: JobTitleQuestionProps) => {
  const { t } = useTranslations(translations);

  const [suggestions, setSuggestions] = useState<AutosuggestValue[]>([]);
  const [searchTerm, setSearchTerm] = useState<AutosuggestValue>({
    text: value?.text || '',
    value: value?.id,
  });

  useEffect(() => {
    if (value?.text !== searchTerm.text) {
      setSearchTerm({
        text: value?.text || '',
        value: value?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 400);

  const handleOnChangeAutosuggest = (event: AutosuggestValue) => {
    setSearchTerm(event);
    onChange({ text: event.text });

    if (event.text) {
      setIsJobTitleUpdating?.(true);
      onValueSelected();
    }

    if (!event.text || event.text.length < MINIMUM_CHARACTERS_SEARCH) {
      setSuggestions([]);
      return;
    }
  };

  const onClear = () => {
    setIsJobTitleUpdating?.(false);
    setSearchTerm({ text: '' });
    onChange({});
    setSuggestions([]);
  };

  useEffect(() => {
    const fetchJobTitles = async (text: string) => {
      try {
        setIsJobTitleUpdating?.(true);
        const allRoleTitles = await getRoleTitles(text);

        const roleSuggestions = allRoleTitles.map((role) => ({
          text: role.normalisedText,
          value: role.id,
        }));

        setSuggestions(roleSuggestions);

        onChange({
          text: searchTerm.text,
          id: roleSuggestions.find(
            (option) =>
              option.text.toLowerCase() === searchTerm.text.toLowerCase(),
          )?.value,
        });
      } finally {
        setIsJobTitleUpdating?.(false);
      }
    };

    const debouncedSearchTermText = debouncedSearchTerm.text?.trim();
    if (
      debouncedSearchTermText &&
      debouncedSearchTermText.length >= MINIMUM_CHARACTERS_SEARCH &&
      debouncedSearchTermText.length <= JOB_TITLE_MAX_LENGTH
    ) {
      void fetchJobTitles(debouncedSearchTermText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  return (
    <Autosuggest
      id="jobTitle"
      label={t('Job title')}
      aria-label={t('Job title')}
      description={description}
      icon={<IconSearch />}
      value={searchTerm}
      onChange={handleOnChangeAutosuggest}
      onClear={onClear}
      suggestions={filterSuggestions(suggestions)}
      tone={tone}
      message={message}
      suggestionHighlight="matching"
      reserveMessageSpace={true}
      automaticSelection={
        suggestions[0]?.text.toLowerCase() === searchTerm.text.toLowerCase()
      }
    />
  );
};
